import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { Operator } from '@/schemas/users';

const schema = z.object({
  password: z.string().min(8, 'errors.validation.passwordTooShort'),
});

type FormValues = z.infer<typeof schema>;

interface ResetPasswordDialogProps {
  user: Operator | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (password: string) => void;
  isLoading?: boolean;
}

export default function ResetPasswordDialog({
  user,
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: ResetPasswordDialogProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  React.useEffect(() => {
    if (open) {
      reset();
      setShowPassword(false);
    }
  }, [open, reset]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('operators.resetDialog.title')}</DialogTitle>
          <DialogDescription asChild>
            <div>
              <Trans
                i18nKey="operators.resetDialog.description"
                values={{ username: user.username }}
                components={{ strong: <strong /> }}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((data) => onSubmit(data.password))} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-password">{t('operators.resetDialog.password')}</Label>
            <div className="relative">
              <Input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="pr-10 font-mono"
                {...register('password')}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={
                  showPassword
                    ? t('operators.resetDialog.hidePassword')
                    : t('operators.resetDialog.showPassword')
                }
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive" role="alert">
                {t(String(errors.password.message))}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('operators.resetDialog.resetting')}
                </>
              ) : (
                t('operators.resetDialog.reset')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
